import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebaseConfig';
import ZxingBarcodeScanner from './ZxingBarcodeScanner';
import { isSimpleProductCountry } from '../utils/countryConfig';
import '../styles.css';

const INITIAL_RESULT_LIMIT = 30;
const CLOUD_SEARCH_LIMIT = 60;
const IMAGE_SEARCH_LIMIT = 12;

const compressImageFile = (file, maxSize = 900, quality = 0.82) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const image = new Image();
    image.onload = () => {
      const scale = Math.min(1, maxSize / Math.max(image.width, image.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(image.width * scale));
      canvas.height = Math.max(1, Math.round(image.height * scale));
      const context = canvas.getContext('2d');
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    image.onerror = () => reject(new Error('圖片讀取失敗'));
    image.src = reader.result;
  };
  reader.onerror = () => reject(new Error('圖片讀取失敗'));
  reader.readAsDataURL(file);
});

function ClientSideSearch() {
  const { country } = useParams();
  const isCrystal = isSimpleProductCountry(country);
  const navigate = useNavigate();
  const location = useLocation();

  // 搜尋相關 state
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [showAddProductPrompt, setShowAddProductPrompt] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [productLoadError, setProductLoadError] = useState('');
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const [isImageSearching, setIsImageSearching] = useState(false);
  const [imageSearchPreview, setImageSearchPreview] = useState('');
  const imageInputRef = useRef(null);
  const [visibleResultCount, setVisibleResultCount] = useState(INITIAL_RESULT_LIMIT);
  const searchTermRef = useRef('');
  const lastAppliedLocationSearchRef = useRef(null);

  // 直接預設為方格模式 (grid)
  const [viewMode, setViewMode] = useState('grid');

  useEffect(() => {
    searchTermRef.current = searchTerm;
  }, [searchTerm]);

  // 用雲端索引搜尋符合條件的商品，避免手機端每次下載完整商品庫。
  const fetchAndFilterProducts = useCallback(async (termInput) => {
    // 將搜尋字串轉成大寫
    const term = termInput.trim().toUpperCase();
    if (!term) {
      setFilteredProducts([]);
      setShowAddProductPrompt(false);
      setIsSearching(false);
      setImageSearchPreview('');
      setVisibleResultCount(INITIAL_RESULT_LIMIT);
      return;
    }

    setIsSearching(true);
    setIsLoadingProducts(true);
    setProductLoadError('');
    setShowAddProductPrompt(false);
    setImageSearchPreview('');

    try {
      const searchProducts = httpsCallable(functions, 'searchProducts');
      const response = await searchProducts({
        country,
        term,
        limit: CLOUD_SEARCH_LIMIT,
      });
      const cloudResults = response.data?.results || [];
      setVisibleResultCount(INITIAL_RESULT_LIMIT);
      setFilteredProducts(cloudResults);
      setShowAddProductPrompt(cloudResults.length === 0);
      setIsSearching(false);
      setIsLoadingProducts(false);
      return cloudResults;
    } catch (error) {
      console.error('Cloud search failed:', error);
      setProductLoadError('雲端搜尋暫時無法使用，請稍後再試或手動更新商品索引');
      setIsSearching(false);
      setIsLoadingProducts(false);
      return null;
    }
  }, [country]);

  // 添加輸入框修復
  useEffect(() => {
    // 確保ProductList頁面的輸入框可以正常使用各國輸入法
    const style = document.createElement('style');
    style.id = 'productlist-input-fix';
    style.textContent = `
      /* ProductList頁面輸入框修復 */
      input[type="text"] {
        pointer-events: auto !important;
        -webkit-user-select: text !important;
        -moz-user-select: text !important;
        -ms-user-select: text !important;
        user-select: text !important;
        -webkit-touch-callout: default !important;
        ime-mode: active !important;
        -webkit-ime-mode: active !important;
        -moz-ime-mode: active !important;
        -ms-ime-mode: active !important;
        text-transform: none !important;
        -webkit-text-transform: none !important;
        -moz-text-transform: none !important;
        -ms-text-transform: none !important;
        -webkit-user-modify: read-write !important;
        -moz-user-modify: read-write !important;
        user-modify: read-write !important;
        cursor: text !important;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      // 清理動態添加的樣式
      const productListInputFixStyle = document.getElementById('productlist-input-fix');
      if (productListInputFixStyle) {
        document.head.removeChild(productListInputFixStyle);
      }
    };
  }, []);

  // 從 URL 讀取搜尋字串，並在初始載入時若有參數就執行搜尋
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const term = params.get('search') || '';
    const isNewLocationSearch = lastAppliedLocationSearchRef.current !== location.search;

    if (isNewLocationSearch) {
      lastAppliedLocationSearchRef.current = location.search;

      if (term) {
        // 只有 URL 真的改變時才回填輸入框，避免使用者刪字時被即時同步資料重設。
        searchTermRef.current = term;
        setSearchTerm(term);
        fetchAndFilterProducts(term);
      } else {
        searchTermRef.current = '';
        setSearchTerm('');
        setFilteredProducts([]);
        setShowAddProductPrompt(false);
        setVisibleResultCount(INITIAL_RESULT_LIMIT);
      }
      return;
    }

    if (term && searchTermRef.current.trim() === term) {
      fetchAndFilterProducts(term);
    }
  }, [location.search, fetchAndFilterProducts]);

  // 添加輸入框修復
  useEffect(() => {
    // 簡化的輸入框修復 - 只修復關鍵問題
    const style = document.createElement('style');
    style.id = 'productlist-main-input-fix';
    style.textContent = `
      /* ProductList頁面輸入框簡化修復 */
      input[type="text"] {
        text-transform: none !important;
        ime-mode: active;
        -webkit-ime-mode: active;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      // 清理動態添加的樣式
      const productListMainInputFixStyle = document.getElementById('productlist-main-input-fix');
      if (productListMainInputFixStyle) {
        document.head.removeChild(productListMainInputFixStyle);
      }
    };
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearchClick();
    }
  };

  const handleSearchClick = () => {
    const term = searchTerm.trim();
    const nextSearch = `?search=${encodeURIComponent(term)}`;

    if (location.search === nextSearch) {
      fetchAndFilterProducts(term);
      return;
    }

    navigate(nextSearch);
  };

  const handleBarcodeScan = useCallback(async (barcode) => {
    const nextTerm = barcode.trim();
    const nextSearch = `?search=${encodeURIComponent(nextTerm)}`;

    setSearchTerm(nextTerm);
    setIsScannerOpen(false);

    const results = await fetchAndFilterProducts(nextTerm);
    if (results && results.length === 1) {
      navigate(`/view/${country}/${results[0].id}?search=${encodeURIComponent(nextTerm)}`);
      return;
    }

    if (location.search === nextSearch) {
      return;
    }

    navigate(nextSearch);
  }, [country, fetchAndFilterProducts, location.search, navigate]);

  const handleImageSearchFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    try {
      const image = await compressImageFile(file);
      setImageSearchPreview(image);
      await runImageSearch(image, searchTermRef.current);
    } catch (error) {
      console.error('Image search failed:', error);
      setProductLoadError('圖片搜尋暫時無法使用，請稍後再試或更新商品圖片索引');
      setIsImageSearching(false);
    }
  };

  const runImageSearch = async (image, keyword = '') => {
    const trimmedKeyword = String(keyword || '').trim();

    setIsImageSearching(true);
    setProductLoadError('');
    setShowAddProductPrompt(false);

    try {
      const searchProductsByImage = httpsCallable(functions, 'searchProductsByImage');
      const response = await searchProductsByImage({
        country,
        image,
        term: trimmedKeyword,
        limit: IMAGE_SEARCH_LIMIT,
      });
      const results = response.data?.results || [];
      setFilteredProducts(results);
      setVisibleResultCount(INITIAL_RESULT_LIMIT);
      setShowAddProductPrompt(results.length === 0);
    } catch (error) {
      console.error('Image search failed:', error);
      setProductLoadError('圖片搜尋暫時無法使用，請稍後再試或更新商品圖片索引');
    } finally {
      setIsImageSearching(false);
    }
  };

  const handleImageSearchClick = async () => {
    if (imageSearchPreview) {
      await runImageSearch(imageSearchPreview, searchTerm);
      return;
    }
    imageInputRef.current?.click();
  };

  const handleSearchNext = () => {
    setImageSearchPreview('');
    searchTermRef.current = '';
    setSearchTerm('');
    setFilteredProducts([]);
    setShowAddProductPrompt(false);
    setVisibleResultCount(INITIAL_RESULT_LIMIT);
    setProductLoadError('');

    if (location.search) {
      navigate(location.pathname);
    }
  };

  const handleToggleView = () => {
    setViewMode((prev) => (prev === 'grid' ? 'list' : 'grid'));
  };

  const handleAddProduct = () => {
    navigate(
      `/add/${country}?barcode=${encodeURIComponent(
        searchTerm
      )}&search=${encodeURIComponent(searchTerm)}`
    );
  };

  const visibleProducts = filteredProducts.slice(0, visibleResultCount);
  const hasMoreResults = filteredProducts.length > visibleResultCount;
  const currentSearch = searchTerm.trim();
  const hasImageSearchPreview = Boolean(imageSearchPreview);
  const imageSearchButtonLabel = isImageSearching
    ? '正在分析圖片...'
    : hasImageSearchPreview
      ? (currentSearch ? '重新圖搜（含品名）' : '重新圖搜')
      : (currentSearch ? 'Ai圖搜圖（含品名）' : 'Ai圖搜圖');

  const pageStyles = {
    wrapper: {
      minHeight: '100vh',
      background: '#f5f6fa',
      padding: '1.25rem 0',
    },
    container: {
      background: '#ffffff',
      margin: '0 auto',
      maxWidth: '440px',
      borderRadius: '24px',
      padding: '1.5rem',
      boxShadow: '0 18px 38px rgba(15, 24, 45, 0.12)',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: '1rem',
    },
    title: {
      fontSize: '1.35rem',
      fontWeight: 600,
      color: '#141414',
    },
    tag: {
      fontSize: '0.85rem',
      padding: '0.35rem 0.65rem',
      borderRadius: '999px',
      background: '#eef2ff',
      color: '#4751c4',
    },
    searchCard: {
      background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
      borderRadius: '18px',
      padding: '1rem',
      marginBottom: '1.25rem',
      border: '1px solid rgba(15,24,45,0.06)',
    },
    searchHint: {
      fontSize: '0.82rem',
      color: '#64748b',
      marginBottom: '0.75rem',
      lineHeight: 1.45,
    },
    input: {
      flex: 1,
      minWidth: 0,
      height: '100%',
      border: 'none',
      outline: 'none',
      padding: '0 0.25rem 0 1rem',
      fontSize: '1.05rem',
      lineHeight: '1.2',
      background: 'transparent',
      marginBottom: 0,
      textAlign: 'center',
      boxSizing: 'border-box',
    },
    searchButton: {
      width: '100%',
      borderRadius: '12px',
      border: 'none',
      padding: '0.82rem',
      fontSize: '1rem',
      fontWeight: 600,
      color: '#fff',
      background: '#111827',
      cursor: 'pointer',
      transition: 'all 0.2s',
      marginBottom: '0.55rem',
    },
    imageSearchButton: {
      width: '100%',
      borderRadius: '12px',
      border: '1px solid #c7d2fe',
      padding: '0.82rem',
      fontSize: '1rem',
      fontWeight: 700,
      color: '#4338ca',
      background: '#eef2ff',
      cursor: 'pointer',
      transition: 'all 0.2s',
      marginBottom: '0.55rem',
    },
    searchNextButton: {
      width: '100%',
      borderRadius: '12px',
      border: '1px solid #86efac',
      padding: '0.82rem',
      fontSize: '1rem',
      fontWeight: 700,
      color: '#166534',
      background: '#f0fdf4',
      cursor: 'pointer',
      transition: 'all 0.2s',
      marginBottom: '0.55rem',
    },
    emptyBanner: {
      textAlign: 'center',
      background: '#fff7ed',
      borderRadius: '12px',
      padding: '1rem',
      marginBottom: '1rem',
      border: '1px dashed #fdba74',
    },
    addBtn: {
      marginTop: '0.6rem',
      borderRadius: '10px',
      border: 'none',
      padding: '0.7rem 1.25rem',
      fontSize: '1rem',
      fontWeight: 600,
      color: '#fff',
      background: '#f97316',
      cursor: 'pointer',
    },
    scannerButton: {
      width: '40px',
      height: '40px',
      flex: '0 0 40px',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '10px',
      border: 'none',
      padding: 0,
      marginRight: '6px',
      color: '#1d4ed8',
      background: isScannerOpen ? '#dbeafe' : 'transparent',
      cursor: 'pointer',
      transition: 'background 0.15s ease',
    },
    scannerIcon: {
      width: '22px',
      height: '22px',
      display: 'block',
      flexShrink: 0,
    },
    searchInputRow: {
      display: 'flex',
      alignItems: 'center',
      height: '52px',
      marginBottom: '0.75rem',
      borderRadius: '12px',
      border: '1px solid transparent',
      background: '#fff',
      boxShadow: '0 8px 20px rgba(31,45,61,0.08)',
      overflow: 'hidden',
    },
    scannerPanel: {
      background: '#fff',
      borderRadius: '16px',
      padding: '0.85rem',
      marginBottom: '1rem',
      border: '1px solid #e5e7eb',
      boxShadow: '0 10px 24px rgba(15, 23, 42, 0.08)',
    },
    resultSummary: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '0.75rem',
      color: '#64748b',
      fontSize: '0.88rem',
      marginBottom: '0.85rem',
    },
    matchBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      alignSelf: 'flex-start',
      padding: '0.18rem 0.45rem',
      borderRadius: '999px',
      background: '#e0f2fe',
      color: '#0369a1',
      fontSize: '0.75rem',
      fontWeight: 600,
      marginBottom: '0.35rem',
    },
    imagePlaceholder: {
      width: '100%',
      aspectRatio: '1 / 1',
      borderRadius: '10px',
      background: '#f1f5f9',
      color: '#94a3b8',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '0.85rem',
    },
    imageSearchPreview: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.65rem',
      padding: '0.65rem',
      marginBottom: '0.75rem',
      borderRadius: '12px',
      background: '#fff',
      border: '1px solid #e0e7ff',
      color: '#475569',
      fontSize: '0.85rem',
      fontWeight: 600,
    },
    imageSearchPreviewImage: {
      width: '56px',
      height: '56px',
      borderRadius: '10px',
      objectFit: 'cover',
      background: '#f1f5f9',
      flexShrink: 0,
    },
    showMoreButton: {
      width: '100%',
      borderRadius: '12px',
      border: '1px solid #cbd5e1',
      background: '#fff',
      color: '#334155',
      padding: '0.85rem',
      fontSize: '0.95rem',
      fontWeight: 600,
      cursor: 'pointer',
      marginTop: '1rem',
    },
    emptyText: {
      margin: '0 0 0.35rem',
      color: '#c2410c',
      fontWeight: 700,
    },
    emptySubText: {
      margin: 0,
      color: '#9a3412',
      fontSize: '0.9rem',
      lineHeight: 1.45,
    },
    listGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
      gap: '1rem',
    },
    listPlain: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.85rem',
    },
    card: {
      borderRadius: '14px',
      padding: '0.75rem',
      border: '1px solid rgba(15,24,45,0.08)',
      background: '#fff',
      boxShadow: '0 8px 20px rgba(14, 21, 47, 0.06)',
      display: 'flex',
      flexDirection: 'column',
      cursor: 'pointer',
      transition: 'transform 0.12s ease, box-shadow 0.12s ease',
      overflow: 'hidden',
    },
    listCard: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: '0.75rem',
    },
    productImage: {
      width: '100%',
      aspectRatio: '1 / 1',
      borderRadius: '10px',
      objectFit: 'cover',
      background: '#f1f1f1',
      display: 'block',
      height: 'auto',
    },
    listImage: {
      width: '72px',
      height: '72px',
      aspectRatio: '1 / 1',
      margin: 0,
      borderRadius: '10px',
      objectFit: 'cover',
    },
    productName: {
      fontSize: '1rem',
      fontWeight: 600,
      color: '#151515',
      display: '-webkit-box',
      WebkitLineClamp: 3,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      lineHeight: '1.4',
      maxHeight: '4.2em',
    },
    productMeta: {
      fontSize: '0.9rem',
      color: '#7a7a7a',
      marginTop: '0.25rem',
      lineHeight: 1.35,
    },
  };

  return (
    <div style={pageStyles.wrapper}>
      <div style={pageStyles.container}>
        <header style={pageStyles.header}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <div style={pageStyles.tag}>{isCrystal ? '水晶商品' : `${country?.toUpperCase()} PRODUCTS`}</div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '0.25rem' }}>
              <h2 style={{ ...pageStyles.title, margin: 0 }}>{isCrystal ? 'Ai 圖搜圖' : '商品搜尋'}</h2>
              <div style={{ fontSize: '0.75rem', color: '#999', margin: 0 }}>{isCrystal ? 'Crystal Image Search' : 'Product Search'}</div>
            </div>
          </div>
          <button
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '10px',
              border: 'none',
              background: '#333',
              color: '#fff',
              fontSize: '0.9rem',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onClick={() => navigate('/')}
            onMouseOver={(e) => { e.target.style.background = '#555'; }}
            onMouseOut={(e) => { e.target.style.background = '#333'; }}
          >
            ← 返回首頁
          </button>
        </header>

        <section style={pageStyles.searchCard}>
          <div style={pageStyles.searchHint}>
            {isCrystal
              ? '可只用 Ai 圖搜圖，或先輸入品名/料號關鍵字再圖搜，命中率更高。手鏈方向不固定不影響搜尋。'
              : '可掃條碼或輸入關鍵字搜尋；上傳圖片後若再加品名關鍵字，可提升圖搜命中率。'}
          </div>
          <div style={pageStyles.searchInputRow}>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={(e) => e.target.select()}
              placeholder="輸入商品名稱、料號或條碼末5碼"
              autoComplete="off"
              autoCapitalize="none"
              style={pageStyles.input}
            />
            <button
              type="button"
              aria-label={isScannerOpen ? '關閉掃描' : '手機掃條碼'}
              title={isScannerOpen ? '關閉掃描' : '手機掃條碼'}
              style={pageStyles.scannerButton}
              onClick={() => setIsScannerOpen((prev) => !prev)}
            >
              <img src="/icons/scan.svg" alt="" style={pageStyles.scannerIcon} />
            </button>
          </div>
          {isScannerOpen && (
            <div style={pageStyles.scannerPanel}>
              <ZxingBarcodeScanner scanning={isScannerOpen} onScan={handleBarcodeScan} />
            </div>
          )}
          <button 
            style={{
              ...pageStyles.searchButton,
              opacity: isSearching || productLoadError ? 0.65 : 1,
              cursor: isSearching ? 'wait' : 'pointer',
            }} 
            onClick={handleSearchClick}
            disabled={isSearching || Boolean(productLoadError)}
            onMouseOver={(e) => { e.target.style.background = '#555'; }}
            onMouseOut={(e) => { e.target.style.background = '#333'; }}
          >
            {isSearching ? '搜尋中...' : '搜尋商品 / Enter'}
          </button>
          <input
            ref={imageInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageSearchFile}
            style={{ display: 'none' }}
          />
          <button
            type="button"
            style={{
              ...pageStyles.imageSearchButton,
              opacity: isImageSearching ? 0.65 : 1,
              cursor: isImageSearching ? 'wait' : 'pointer',
            }}
            disabled={isImageSearching}
            onClick={handleImageSearchClick}
          >
            {imageSearchButtonLabel}
          </button>
          {hasImageSearchPreview && (
            <div style={{ fontSize: '0.82rem', color: '#64748b', marginBottom: '0.55rem', lineHeight: 1.45 }}>
              {currentSearch
                ? `已上傳圖片，並以「${currentSearch}」加強圖搜排序。可修改關鍵字後再按一次。`
                : '已上傳圖片。可輸入品名或料號關鍵字後再按一次，提升命中率。'}
            </div>
          )}
          {imageSearchPreview && (
            <div style={pageStyles.imageSearchPreview}>
              <img
                src={imageSearchPreview}
                alt="你上傳的圖片"
                style={pageStyles.imageSearchPreviewImage}
              />
              <span>你上傳的圖片</span>
            </div>
          )}
          {hasImageSearchPreview && (
            <button
              type="button"
              style={{
                ...pageStyles.searchNextButton,
                opacity: isImageSearching ? 0.65 : 1,
                cursor: isImageSearching ? 'wait' : 'pointer',
              }}
              disabled={isImageSearching}
              onClick={handleSearchNext}
            >
              搜下一筆
            </button>
          )}
          <div style={pageStyles.resultSummary}>
            <span>
              {isImageSearching
                ? (currentSearch ? 'AI 圖搜 + 品名分析中' : 'AI 圖搜圖分析中')
                : isLoadingProducts
                  ? '正在查詢雲端索引'
                  : hasImageSearchPreview && currentSearch
                    ? '圖搜 + 品名加權'
                    : '雲端索引搜尋 / AI 圖搜圖'}
            </span>
            {currentSearch && filteredProducts.length > 0 && (
              <span>找到 {filteredProducts.length} 筆</span>
            )}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
            <img
              src={viewMode === 'grid' ? '/icons/list.png' : '/icons/grid.png'}
              alt="切換顯示模式"
              onClick={handleToggleView}
              style={{ width: '32px', height: '32px', cursor: 'pointer' }}
            />
          </div>
        </section>

        {(isSearching || isImageSearching) && (
          <div style={{ textAlign: 'center', color: '#666', marginBottom: '1rem' }}>
            {isImageSearching ? '正在分析圖片並比對相似商品...' : isLoadingProducts ? '正在查詢雲端商品索引...' : '正在搜尋最新商品資料...'}
          </div>
        )}

        {productLoadError && (
          <div style={{ textAlign: 'center', color: '#c2410c', marginBottom: '1rem' }}>
            {productLoadError}
          </div>
        )}

        {!isSearching && showAddProductPrompt && (
          <div style={pageStyles.emptyBanner}>
            <p style={pageStyles.emptyText}>未找到商品</p>
            <p style={pageStyles.emptySubText}>
              可直接用「{currentSearch || '目前輸入'}」新增商品，系統會自動帶入條碼。
            </p>
            <button style={pageStyles.addBtn} onClick={handleAddProduct}>
              新增商品
            </button>
          </div>
        )}

        {filteredProducts.length > 0 && (
          <section aria-label="商品列表">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', marginBottom: '0.75rem' }}>
              <h3 style={{ margin: 0, color: '#333', fontSize: '1rem' }}>商品列表</h3>
              <button type="button" onClick={() => navigate(`/prep/${country}`)} style={{ background: '#333', color: '#fff', border: 'none', borderRadius: '8px', padding: '0.65rem 0.85rem', fontSize: '0.85rem', cursor: 'pointer' }}>直播備貨清單</button>
            </div>
            <div
              className="product-list"
              style={viewMode === 'grid' ? pageStyles.listGrid : pageStyles.listPlain}
            >
              {visibleProducts.map((product) => (
                <div
                  key={product.id}
                  className="product-card"
                  onClick={() =>
                    navigate(`/view/${country}/${product.id}?search=${encodeURIComponent(searchTerm)}`)
                  }
                  style={{
                    ...pageStyles.card,
                    ...(viewMode === 'list' ? pageStyles.listCard : {}),
                  }}
                >
                  {product.image ? (
                    <img
                      src={product.image}
                      alt={product.name}
                      className="product-image-small"
                      loading="lazy"
                      style={
                        viewMode === 'grid'
                          ? pageStyles.productImage
                          : { ...pageStyles.productImage, ...pageStyles.listImage }
                      }
                    />
                  ) : (
                    <div
                      style={
                        viewMode === 'grid'
                          ? pageStyles.imagePlaceholder
                          : { ...pageStyles.imagePlaceholder, ...pageStyles.listImage }
                      }
                    >
                      無圖片
                    </div>
                  )}
                  <div
                    className="product-info"
                    style={{ marginTop: viewMode === 'grid' ? '0.5rem' : 0 }}
                  >
                    {product.matchReason && (
                      <span style={pageStyles.matchBadge}>{product.matchReason}</span>
                    )}
                    <div style={pageStyles.productName}>
                      {product.subProducts && product.subProducts.length > 0 && (
                        <span style={{
                          display: 'inline-block',
                          padding: '0.10125rem 0.30375rem',
                          backgroundColor: '#d32f2f',
                          color: '#fff',
                          borderRadius: '4px',
                          fontSize: pageStyles.productName.fontSize ? `${parseFloat(pageStyles.productName.fontSize) * 0.81}rem` : '0.81rem',
                          fontWeight: '600',
                          whiteSpace: 'nowrap',
                          lineHeight: pageStyles.productName.lineHeight ? `${parseFloat(pageStyles.productName.lineHeight) * 0.81}` : '1.134',
                          marginRight: '0.5rem',
                          verticalAlign: 'baseline'
                        }}>
                          組合
                        </span>
                      )}
                      {product.name && product.name.startsWith('(組)') ? product.name.replace(/^\(組\)/, '') : product.name}
                    </div>
                    <div style={pageStyles.productMeta}>
                      {product.styles.length ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', alignItems: 'center' }}>
                          {product.styles.map((style, idx) => {
                            return (
                              <span key={idx} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                                <span>{style}</span>
                                {idx < product.styles.length - 1 && <span> / </span>}
                              </span>
                            );
                          })}
                        </div>
                      ) : '未設定樣式'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {hasMoreResults && (
              <button
                type="button"
                style={pageStyles.showMoreButton}
                onClick={() => setVisibleResultCount((count) => count + INITIAL_RESULT_LIMIT)}
              >
                顯示更多（目前 {visibleProducts.length} / {filteredProducts.length}）
              </button>
            )}
          </section>
        )}
      </div>
    </div>
  );
}

export default ClientSideSearch;
